import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import {
  buildCatalogSiteOptionVMs,
  buildSiteHierarchyDisplayRows,
  type SiteTreeMeta,
} from '@/lib/catalogSiteTree';
import { cn } from '@/lib/cn';
import { notifySessionRefresh } from '@/lib/sessionEvents';
import { useSession } from '@/hooks/useSession';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';

type SiteItem = SiteTreeMeta & {
  site_code: string;
  enabled: boolean;
};

type ListResp = {
  ok: boolean;
  items: SiteItem[];
  current_site_id: number;
};

type Props = {
  variant?: 'default' | 'compact' | 'mobile';
  className?: string;
  idSuffix?: string;
};

export function SiteSwitcher({ variant = 'default', className, idSuffix = '' }: Props) {
  const { currentSiteId, loading: sessionLoading, refresh } = useSession();
  const [sites, setSites] = useState<SiteItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listErr, setListErr] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSites = useCallback(async () => {
    setListErr(false);
    setListLoading(true);
    try {
      const r = await apiFetch<ListResp>('list_sites', {
        method: 'GET',
        query: { page: 1, per_page: 100 },
      });
      if (r.ok) {
        setSites(r.items);
      } else {
        setListErr(true);
      }
    } catch {
      setListErr(true);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  useEffect(() => {
    const onRefresh = () => {
      void refresh();
      void loadSites();
    };
    window.addEventListener('wes-session-refresh', onRefresh);
    return () => window.removeEventListener('wes-session-refresh', onRefresh);
  }, [refresh, loadSites]);

  async function onSelect(siteId: number) {
    if (siteId === currentSiteId || siteId < 1 || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch<{ ok: boolean }>('switch_site', {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId }),
      });
      if (r.ok) {
        notifySessionRefresh();
        await loadSites();
      }
    } finally {
      setBusy(false);
    }
  }

  const options = sites.filter((s) => s.enabled || s.id === currentSiteId);

  const selectOptions = useMemo(
    () =>
      buildCatalogSiteOptionVMs(
        options.map((s) => ({ id: s.id, site_name: s.site_name })),
        options,
      ),
    [options],
  );

  const currentDepth = useMemo((): 0 | 1 | null => {
    const site = options.find((s) => s.id === currentSiteId);
    if (!site) return null;
    const row = buildSiteHierarchyDisplayRows(options).find((r) => r.site.id === site.id);
    return row?.depth ?? (site.parent_site_id ? 1 : 0);
  }, [options, currentSiteId]);

  const compact = variant === 'compact';
  const mobile = variant === 'mobile';
  const selectId = `wes-site-switcher${idSuffix}`;
  const selectCls = cn(
    'min-w-0 flex-1 rounded-lg border border-slate-200/90 bg-white text-slate-800 shadow-sm outline-none transition',
    'focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20',
    mobile
      ? 'max-w-full py-2 pl-2 pr-8 text-base'
      : compact
        ? 'max-w-full py-1.5 pl-2 pr-7 text-xs'
        : 'max-w-[min(20rem,42vw)] min-w-[9rem] py-1.5 pl-2 pr-8 text-sm',
  );

  const rowCls = cn(
    'flex min-w-0 max-w-full items-center gap-2',
    mobile && 'w-full',
    className,
  );

  if (sessionLoading || listLoading) {
    return (
      <div className={cn(rowCls, 'text-slate-500', mobile ? 'text-sm' : 'text-xs')}>
        {'\u52a0\u8f7d\u7ad9\u70b9\u2026'}
      </div>
    );
  }

  if (listErr) {
    return (
      <div className={cn(rowCls, 'flex-wrap gap-x-2 text-sm')}>
        <span className="text-rose-700/90">{'\u7ad9\u70b9\u5217\u8868\u52a0\u8f7d\u5931\u8d25'}</span>
        <Link to="/sites" className="font-medium text-teal-800 underline underline-offset-2">
          {'\u7ad9\u70b9\u7ba1\u7406'}
        </Link>
      </div>
    );
  }

  return (
    <div className={rowCls}>
      {currentDepth !== null ? <SiteLevelBadge depth={currentDepth} /> : null}
      <select
        id={selectId}
        className={selectCls}
        value={currentSiteId > 0 ? String(currentSiteId) : ''}
        disabled={busy || selectOptions.length === 0}
        aria-busy={busy}
        aria-label={'\u5de5\u4f5c\u7ad9\u70b9'}
        title={'\u5de5\u4f5c\u7ad9\u70b9'}
        onChange={(e) => void onSelect(Number(e.target.value))}
      >
        {selectOptions.length === 0 ? (
          <option value="">{'\u6682\u65e0\u7ad9\u70b9'}</option>
        ) : (
          selectOptions.map((o) => {
            const src = options.find((s) => s.id === o.id);
            const disabled = src ? !src.enabled : false;
            return (
              <option key={o.id} value={o.id} disabled={disabled}>
                {o.treeLabel}
                {src && !src.enabled ? ' (\u5df2\u505c\u7528)' : ''}
              </option>
            );
          })
        )}
      </select>
    </div>
  );
}
